"use client";

import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Divider, Grid } from "@mui/material";
import Link from "next/link";
import React from "react";
import { toast } from "react-toastify";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import { Paragraph } from "src/lib/components/salify-next-layout/default/components/Typography";
import { FlexBetween } from "src/lib/components/salify-next-layout/default/components/flex-box";
import useCart from "src/lib/components/salify-next-layout/default/hooks/useCart";
import { useGlobalContext } from "src/lib/components/salify-next-layout/default/global-context";
import clientFetcher, { OrderPayload, PromotionResultType } from "src/lib/fetcher/client";
import { useCardSectionContext } from "../context";
import CartItem from "../step-cart/cart-item";
import CheckoutAltSummary from "../step-checkout/checkout-alt-summery/checkout-alt-summery";
import { CheckoutForm } from "../step-checkout/checkout-form";

const CardSectionStepReview = (props: React.PropsWithChildren<{ backUrl: string }>) => {
  const { state } = useCart();
  const { formValue } = useCardSectionContext();
  const { cartItems: productList, promotionResult, getPrice } = useGlobalContext();
  const [paymentMethod, setPaymentMethod] = React.useState(undefined);
  const [paymentVendor, setPaymentVendor] = React.useState(undefined);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const price = getPrice();
  const shippingPrice = price.shipping;
  const finalPrice = price.amount;
  const totalPrice = price.price;
  const tax = price.tax;
  const discount = price.discount;
  let label = "Phí vận chuyển";
  if (
    shippingPrice === 0 &&
    promotionResult &&
    promotionResult.code &&
    promotionResult.code.length &&
    promotionResult.resultType === PromotionResultType.FreeShipping
  ) {
    label += " (Mã vận chuyển 0 đồng) ";
  }

  React.useEffect(() => {
    setPaymentMethod(formValue.paymentMethod);
    setPaymentVendor(formValue.paymentVendor);
  }, [formValue.paymentMethod]);

  const renderPaymentMethod = () => {
    if (paymentMethod === 0) {
      return <SalifyFallback.Label path="Cash" default="Thanh toán khi nhận hàng" />;
    } else if (paymentMethod === 1) {
      return <SalifyFallback.Label path="Bank" default="Ngân hàng" />;
    } else if (paymentMethod === 2) {
      return <SalifyFallback.Label path="Card" default="Thẻ tín dụng/Ghi nợ" />;
    } else if (paymentMethod === 3) {
      return <SalifyFallback.Label path="Digital Wallet" default="Ví điện tử" />;
    }

    return <>-</>;
  };

  const renderPaymentVendor = () => {
    if (paymentMethod === 3) {
      if (paymentVendor === 0) {
        return <SalifyFallback.Label path="Momo" default="Momo" />;
      } else if (paymentVendor === 1) {
        return <SalifyFallback.Label path="VN Pay" default="VN Pay" />;
      } else {
        return <>-</>;
      }
    }
    return null;
  };

  return (
    <Grid container spacing={3}>
      <CheckoutForm
        {...props}
        readOnly
        promotionInSidebar
        leadingContent={
          <>
            {state.cart.map(({ product }) => (
              <CartItem key={product.id} product={product} readOnly />
            ))}
          </>
        }
        trailingContent={
          <Grid container spacing={6}>
            <Grid item sm={6} xs={12}>
              <Button
                LinkComponent={Link}
                variant="outlined"
                color="primary"
                type="button"
                href={props.backUrl}
                fullWidth
              >
                <SalifyFallback.Label path="Back to Payment" default="Quay lại" />
              </Button>
            </Grid>

            <Grid item sm={6} xs={12}>
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={submitLoading}
                fullWidth
                onClick={() => {
                  setSubmitLoading(true);

                  const payload: OrderPayload = {
                    paymentMethod: formValue.paymentMethod,
                    paymentVendor: formValue.paymentVendor,
                    data: {
                      currencyCode: 0,
                      paymentMethod: formValue.paymentMethod,
                      paymentVendor: formValue.paymentVendor,
                      purchaseDate: new Date(),
                      storeId: formValue.shipping_store,
                      appliedPromotion: {
                        code: promotionResult?.code,
                      },
                      surchages: 1,
                      totalAmount: finalPrice,
                      orderDetails: productList.map(({ product, quantity }) => ({
                        productId: product.id,
                        discount: 1,
                        discountRatio: 1,
                        price: product.salePrice,
                        totalAmount: product.salePrice * quantity,
                        quantity,
                      })),
                      orderDelivery: {
                        type: 0,
                        price: shippingPrice,
                      },
                      customerInfo: {
                        address: formValue.shipping_address,
                        email: formValue.shipping_email,
                        name: formValue.shipping_fullName,
                        phone: formValue.shipping_phone,
                        gender: true,
                        locationWardId: formValue.shipping_ward,
                      },
                    },
                  };

                  clientFetcher
                    .createOrder(payload)
                    .then(async (result) => {
                      const { error, value: response } = result;

                      if (error) {
                        console.error("[Order] exception", error);

                        toast.error(
                          // TODO: should having translation/label from fallback
                          "Có lỗi xảy ra khi thực hiện order, xin thực hiện tải lại trang hoặc nhấn F5 để thử lại.",
                          { position: "bottom-right" },
                        );

                        return;
                      }

                      await clientFetcher.processOrderResponse(response, payload);
                    })
                    .finally(() => {
                      setSubmitLoading(false);
                    });
                }}
              >
                <SalifyFallback.Label path="Process order" default="Xác nhận đơn hàng" />
              </LoadingButton>
            </Grid>
          </Grid>
        }
        renderSidebar={(promotion) => (
          <>
            <CheckoutAltSummary />
            {promotion}
            <Box component={Divider} borderColor="grey.300" my={3} />

            <Paragraph color="secondary.900" fontWeight={700} mb={2}>
              <SalifyFallback.Label path="Payment information" default="Thông tin thanh toán" />
            </Paragraph>

            <FlexBetween mb={0.5}>
              <Paragraph color="grey.600">
                <SalifyFallback.Label path="Payment method" default="Phương thức thanh toán" />:
              </Paragraph>
              <Paragraph fontWeight="700">{renderPaymentMethod()}</Paragraph>
            </FlexBetween>
            {renderPaymentVendor() ? (
              <FlexBetween mb={0.5}>
                <Paragraph color="grey.600">
                  <SalifyFallback.Label path="Payment vendor" default="Nhà cung cấp thanh toán" />:
                </Paragraph>
                <Paragraph fontWeight="700">{renderPaymentVendor()}</Paragraph>
              </FlexBetween>
            ) : null}
          </>
        )}
      />
    </Grid>
  );
};

export default CardSectionStepReview;
